import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { CropFrameGeom } from '@/lib/photos/crop-frame-resize'
import { resolveCropCommitBefore, resolveCropGestureBefore } from '../helpers/crop-gesture-before'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Crop mode's gestures: frame resize, photo pan/rotate/zoom/flip and shape rotate — each a live
 * per-tick update plus a commit that records one undo step — and Reset back to the shape as crop
 * mode was entered.
 */
export function useCropGestureHandlers({
  internalShapeManagerRef,
  cropShapeData,
  setCropShapeData,
  cropOriginalShapeRef,
  cropGestureBeforeRef,
  onShapeCommitted,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  cropShapeData: ShapeData | null
  setCropShapeData: Dispatch<SetStateAction<ShapeData | null>>
  cropOriginalShapeRef: MutableRefObject<ShapeData | null>
  cropGestureBeforeRef: MutableRefObject<ShapeData | null>
  onShapeCommitted: CenterCanvasConfig['onShapeCommitted']
}) {
  // Live update while dragging a crop-frame resize handle — mirrors handleNodeEditPathChange
  // (`use-node-edit-path-handlers`): applies the shape manager update immediately (so the shape
  // renders the live frame/pan every drag tick) without persisting or touching undo until the
  // drag ends.
  //
  // `mgr.updateShape` internally rescales `path`/`pathCommands` to match a width/height change
  // (`applyPartialShapeUpdate` in shape-manager/helpers/shape-list.ts — any call that changes size without also
  // passing an explicit new path rescales the existing one), but it returns `void`. Unlike
  // `handleNodeEditPathChange` (which already has the new path in hand from its own gesture
  // math), `computeCropFrameResize` never touches path/pathCommands itself — it only computes
  // x/y/width/height — so the rescaled geometry only exists inside the manager until read back.
  // Without reading it back here, `cropShapeData` (the `shape` prop `CropOverlay` uses for its
  // own visual-bounds box, and the object `handleCropFrameCommit` persists) keeps the
  // crop-mode-entry path/pathCommands frozen while width/height keep changing — a real path
  // baked for the OLD size, divided by the NEW size in `visualInsetRatiosForShape`, drifting the
  // displayed selection box further from the actually-rendered (correctly rescaled) shape with
  // every resize tick, and — if left uncorrected through to commit — persisting a path that
  // doesn't match the shape's own saved width/height.
  //
  // Also captures `cropGestureBeforeRef` on the FIRST tick of a gesture (while still null) —
  // `cropShapeData` gets overwritten with the live in-progress geometry on every tick below, so
  // by the time the matching *Commit handler runs it already equals the POST-gesture state.
  // Using it there as the undo "before" snapshot made before === after, so home.tsx's diff found
  // nothing to record and Ctrl+Z silently no-opped for every crop-mode drag (resize/pan/rotate).
  const handleCropFrameChange = useCallback(
    (shapeId: string, geom: CropFrameGeom, photoFill: ShapePhotoFill) => {
      const mgr = internalShapeManagerRef.current

      cropGestureBeforeRef.current = resolveCropGestureBefore(
        cropGestureBeforeRef.current,
        cropShapeData,
        shapeId
      )

      mgr?.updateShape(shapeId, { ...geom, photoFill })
      const rescaled = mgr?.getShapes().find((s) => s.id === shapeId)

      setCropShapeData((prev) =>
        prev && prev.id === shapeId
          ? {
              ...prev,
              ...geom,
              photoFill,
              ...(rescaled ? { path: rescaled.path, pathCommands: rescaled.pathCommands } : {}),
            }
          : prev
      )
    },
    [cropShapeData, internalShapeManagerRef, cropGestureBeforeRef, setCropShapeData]
  )

  const handleCropFrameCommit = useCallback(
    (shapeId: string, geom: CropFrameGeom, photoFill: ShapePhotoFill) => {
      const mgr = internalShapeManagerRef.current
      const existing = resolveCropCommitBefore(
        cropGestureBeforeRef.current,
        cropOriginalShapeRef.current,
        cropShapeData,
        shapeId
      )

      cropGestureBeforeRef.current = null

      if (!mgr || !existing) return

      mgr.updateShape(shapeId, { ...geom, photoFill })
      const rescaled = mgr.getShapes().find((s) => s.id === shapeId)
      const updated = {
        ...existing,
        ...geom,
        photoFill,
        ...(rescaled ? { path: rescaled.path, pathCommands: rescaled.pathCommands } : {}),
      }

      setCropShapeData(updated)

      const undoMeta: ShapeCommittedUndoMeta = {
        before: {
          x: existing.x,
          y: existing.y,
          width: existing.width,
          height: existing.height,
          photoFill: existing.photoFill,
        },
        after: {
          x: updated.x,
          y: updated.y,
          width: updated.width,
          height: updated.height,
          photoFill: updated.photoFill,
        },
      }

      onShapeCommitted?.(updated, 'update', undoMeta)
    },
    [
      cropShapeData,
      onShapeCommitted,
      internalShapeManagerRef,
      cropGestureBeforeRef,
      cropOriginalShapeRef,
      setCropShapeData,
    ]
  )

  // Generic "apply this photoFill to the shape being cropped" — shared by CropOverlay's
  // interior drag-to-pan, its rotate handle, CropZoomSlider's zoom slider, and CropToolbar's
  // Flip Vertical/Horizontal buttons, all of which compute a full replacement `ShapePhotoFill`
  // themselves (pan delta, new rotation, scale + re-clamped offset, or a toggled flippedX/Y)
  // rather than needing dedicated handlers per gesture. Only `handlePhotoPanCommit` (not the
  // live-update half) is used by the flip buttons — a flip is an instant, one-shot toggle with
  // no drag/preview phase, so it never touches `cropGestureBeforeRef` below (nothing to capture
  // — `cropShapeData` at that point genuinely IS the pre-flip state).
  //
  // Captures `cropGestureBeforeRef` on the first tick of a drag (pan or rotate), same reasoning
  // as `handleCropFrameChange` above: `cropShapeData` is live-mutated every tick, so it can't
  // also serve as the commit's undo "before" snapshot.
  const handlePhotoPanChange = useCallback(
    (shapeId: string, photoFill: ShapePhotoFill) => {
      const mgr = internalShapeManagerRef.current

      cropGestureBeforeRef.current = resolveCropGestureBefore(
        cropGestureBeforeRef.current,
        cropShapeData,
        shapeId
      )

      mgr?.updateShape(shapeId, { photoFill })
      setCropShapeData((prev) => (prev && prev.id === shapeId ? { ...prev, photoFill } : prev))
    },
    [cropShapeData, internalShapeManagerRef, cropGestureBeforeRef, setCropShapeData]
  )

  const handlePhotoPanCommit = useCallback(
    (shapeId: string, photoFill: ShapePhotoFill) => {
      const mgr = internalShapeManagerRef.current
      const existing = resolveCropCommitBefore(
        cropGestureBeforeRef.current,
        cropOriginalShapeRef.current,
        cropShapeData,
        shapeId
      )

      cropGestureBeforeRef.current = null

      if (!mgr || !existing) return

      const updated = { ...existing, photoFill }

      mgr.updateShape(shapeId, { photoFill })
      setCropShapeData(updated)

      const undoMeta: ShapeCommittedUndoMeta = {
        before: { photoFill: existing.photoFill },
        after: { photoFill: updated.photoFill },
      }

      onShapeCommitted?.(updated, 'update', undoMeta)
    },
    [
      cropShapeData,
      onShapeCommitted,
      internalShapeManagerRef,
      cropGestureBeforeRef,
      cropOriginalShapeRef,
      setCropShapeData,
    ]
  )

  // Rotates the SHAPE itself (its own path/frame), independent of handlePhotoPanChange/Commit
  // above (which only ever touch photoFill — the photo content's rotation, pan, scale, flips).
  // Mirrors handleCropFrameChange/Commit's cropGestureBeforeRef usage for the same reason: a
  // shape-rotate is a live drag (CropOverlay's new "outer" rotate handle), so cropShapeData is
  // mutated on every tick and can't also serve as the commit's undo "before" snapshot.
  const handleShapeRotateChange = useCallback(
    (shapeId: string, rotation: number) => {
      const mgr = internalShapeManagerRef.current

      cropGestureBeforeRef.current = resolveCropGestureBefore(
        cropGestureBeforeRef.current,
        cropShapeData,
        shapeId
      )

      mgr?.updateShape(shapeId, { rotation })
      setCropShapeData((prev) => (prev && prev.id === shapeId ? { ...prev, rotation } : prev))
    },
    [cropShapeData, internalShapeManagerRef, cropGestureBeforeRef, setCropShapeData]
  )

  const handleShapeRotateCommit = useCallback(
    (shapeId: string, rotation: number) => {
      const mgr = internalShapeManagerRef.current
      const existing = resolveCropCommitBefore(
        cropGestureBeforeRef.current,
        cropOriginalShapeRef.current,
        cropShapeData,
        shapeId
      )

      cropGestureBeforeRef.current = null

      if (!mgr || !existing) return

      const updated = { ...existing, rotation }

      mgr.updateShape(shapeId, { rotation })
      setCropShapeData(updated)

      const undoMeta: ShapeCommittedUndoMeta = {
        before: { rotation: existing.rotation ?? 0 },
        after: { rotation: updated.rotation ?? 0 },
      }

      onShapeCommitted?.(updated, 'update', undoMeta)
    },
    [
      cropShapeData,
      onShapeCommitted,
      internalShapeManagerRef,
      cropGestureBeforeRef,
      cropOriginalShapeRef,
      setCropShapeData,
    ]
  )

  const handleCropReset = useCallback(() => {
    const original = cropOriginalShapeRef.current

    if (!original || !cropShapeData) return

    const mgr = internalShapeManagerRef.current

    if (!mgr) return

    const existing = mgr.getShapes().find((s) => s.id === original.id)

    if (!existing) return

    const restoredShape = {
      ...existing,
      x: original.x,
      y: original.y,
      width: original.width,
      height: original.height,
      rotation: original.rotation,
      photoFill: original.photoFill,
    }

    mgr.updateShape(original.id, {
      x: restoredShape.x,
      y: restoredShape.y,
      width: restoredShape.width,
      height: restoredShape.height,
      rotation: restoredShape.rotation,
      photoFill: restoredShape.photoFill,
    })

    const undoMeta: ShapeCommittedUndoMeta = {
      before: {
        x: existing.x,
        y: existing.y,
        width: existing.width,
        height: existing.height,
        rotation: existing.rotation ?? 0,
        photoFill: existing.photoFill,
      },
      after: {
        x: restoredShape.x,
        y: restoredShape.y,
        width: restoredShape.width,
        height: restoredShape.height,
        rotation: restoredShape.rotation ?? 0,
        photoFill: restoredShape.photoFill,
      },
    }

    onShapeCommitted?.(restoredShape, 'update', undoMeta)
    // Stay in crop mode so the user can keep adjusting after a reset.
    setCropShapeData(restoredShape)
  }, [
    cropShapeData,
    onShapeCommitted,
    internalShapeManagerRef,
    cropOriginalShapeRef,
    setCropShapeData,
  ])

  return {
    handleCropFrameChange,
    handleCropFrameCommit,
    handlePhotoPanChange,
    handlePhotoPanCommit,
    handleShapeRotateChange,
    handleShapeRotateCommit,
    handleCropReset,
  }
}
